// Getting started → Team — the data behind /getting-started/team (POB S9,
// pages/gettingStartedTeam.js): the people the onboarding research read off
// the company's OWN team page after sign-up, the owner's invites to them, and
// the company-domain join switch. The onboarding lane owns the server and
// these SDK names (POB plan §7b-3):
//   listOnboardingPeople(wsId)  → { people: [{ id, name, role, sourceUrl,
//                                  fetchedAt, expiresAt, invitedAt }],
//                                  domain, domainMatchesOwner }
//   removeOnboardingPerson(wsId, personId)               → { removed: true }
//   inviteOnboardingPerson(wsId, personId, { email, recipientName })
//                               → { invitation, person } — the server sends
//                                 the plain workspace invite, role editor,
//                                 and marks the row used; 400 `invalid_email`
//   getDomainJoin(wsId) / setDomainJoin(wsId, enabled)
//                               → { domain, enabled, eligible };
//                                 409 `domain_not_eligible`
// Someone the research did not find goes through the existing workspace
// invite (createWorkspaceInvitation, role editor). A found person never does:
// that call drops every key but email / role / recipientName / teams, so the
// server could not mark the row used.
//
// Privacy (plan §6, picks D3 and D5): every switch starts OFF and turns ON
// only beside an e-mail the owner typed; nothing here reads, guesses or
// prefills an address; a row nobody invites expires on the server after 30
// days. The domain switch exists only when the server says the owner's own
// verified e-mail is on that domain (`eligible`).
//
// Until the server answers — the SDK has no such method yet, or the route
// answers 404/501 — the page says "Soon" and offers no button (plan §7b-2:
// no dead buttons). A 403 is the manager gate.
//
// Root keys (state-app.js, the workspace-scoped wipe): the read
// (`gettingStartedTeamStatus` idle|loading|ready|soon|denied|error,
// `gettingStartedPeople`, `gettingStartedDomain`, `gettingStartedDomainJoin`
// + its `…Status`), the owner's draft per person
// (`gettingStartedInvites` { [personId]: { email, on, status, error } } —
// every write carries all four fields, because a write REPLACES the object
// a key holds — smbls 3.14.870 store — so a left-out field would be lost),
// the "someone else" field
// (`gettingStartedOther` { email, status, error }), the send flag + its last
// result, and the "Why do I see this?" disclosure.
//
// Row fields are `person*`-named on purpose: a `childrenAs: 'state'` row
// falls back to ROOT keys for a field it does not own, and the root has its
// own `name`, `role` and `status`.
import _wsShared from '@symbo-ls/workspace-shared'

// Module helpers carry a `_gst` prefix: frank hoists a functions file's
// module helpers into the project's one shared scope by name.
const _gstRootOf = (root, ctx) => root || (ctx && ctx.state && ctx.state.root) || null
// The tab's own workspace beats the session claim (it lags a switch); either
// id shape — the scopedClient.js `activeWorkspaceId` rule, copied so the
// switch tail can call these loaders without an element.
const _gstWsId = (r) =>
  String(
    (r && r.workspace && (r.workspace.id || r.workspace._id)) ||
      (r && r.jwtClaims && r.jwtClaims.active_workspace_id) ||
      ''
  )
const _gstSdk = (seam, ctx) =>
  (seam && seam.sdk) || (ctx && ctx.sdk) || _wsShared.functions.getSDK()
const _gstHas = (sdk, name) => !!sdk && typeof sdk[name] === 'function'
// The SDK raises an HTTP failure with `status` and the JSON body as `cause`;
// the server names the reason in `error` (or `code`).
const _gstCode = (err) => (err && err.cause && (err.cause.error || err.cause.code)) || ''
const _gstNotThere = (err) => !!err && (err.status === 404 || err.status === 501)
const _gstEmailOk = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
const _gstDraftOf = (r, personId) => {
  const d = (r && r.gettingStartedInvites && r.gettingStartedInvites[personId]) || {}
  return {
    email: typeof d.email === 'string' ? d.email : '',
    on: !!d.on,
    status: d.status || '',
    error: d.error || ''
  }
}
const _gstPerson = (p) => ({
  personId: String((p && (p.id || p._id)) || ''),
  personName: String((p && p.name) || ''),
  personRole: String((p && p.role) || ''),
  personSource: String((p && p.sourceUrl) || ''),
  invitedAt: (p && p.invitedAt) || null
})

// el.call('loadGettingStartedTeam', s.root) — the page's onCreate, and the
// shell's switch tail (functions/loadPrivateData.js) while the page is on
// screen, with `{ sdk, write: put }` (its epoch-guarded writer).
export async function loadGettingStartedTeam(root, via) {
  const r = _gstRootOf(root, this)
  if (!r) return
  const seam = via && typeof via === 'object' ? via : {}
  // A cold boot that settles on another workspace later re-runs the read
  // (the History loader's boot-identity rule).
  _wsShared.functions.onWorkspaceIdentityCorrected(loadGettingStartedTeam)
  const wsId = _gstWsId(r)
  if (!wsId) {
    _wsShared.functions.onActiveWorkspaceFull(loadGettingStartedTeam)
    return
  }
  const write = typeof seam.write === 'function' ? seam.write : (patch) => r.update(patch)
  // A reply that outlived its workspace never lands.
  const land = (patch) => {
    if (_gstWsId(r) === wsId) write(patch)
  }
  const sdk = _gstSdk(seam, this)
  if (!_gstHas(sdk, 'listOnboardingPeople')) {
    land({ gettingStartedTeamStatus: 'soon' })
    return
  }
  land({ gettingStartedTeamStatus: 'loading' })
  try {
    const [res, join] = await Promise.all([
      sdk.listOnboardingPeople(wsId),
      // The join switch is optional: no route yet = no switch, never a
      // failed page.
      _gstHas(sdk, 'getDomainJoin') ? sdk.getDomainJoin(wsId).catch(() => null) : null
    ])
    const body = (res && res.data && Array.isArray(res.data.people) ? res.data : res) || {}
    const people = (Array.isArray(body.people) ? body.people : [])
      .map(_gstPerson)
      .filter((p) => p.personId)
    const domain = String(body.domain || (join && join.domain) || '')
    land({
      gettingStartedTeamStatus: 'ready',
      gettingStartedPeople: people,
      gettingStartedDomain: domain,
      gettingStartedDomainJoin: join
        ? {
            domain: String(join.domain || domain),
            enabled: !!join.enabled,
            eligible: !!join.eligible
          }
        : null,
      gettingStartedDomainJoinStatus: ''
    })
  } catch (err) {
    if (_gstNotThere(err)) {
      land({ gettingStartedTeamStatus: 'soon' })
      return
    }
    if (err && err.status === 403) {
      land({ gettingStartedTeamStatus: 'denied' })
      return
    }
    console.error('[getting-started/team]', err)
    land({ gettingStartedTeamStatus: 'error' })
  }
}

// The rows the list renders — the found people only. The owner's draft is a
// separate key each row reads for itself (gettingStartedDraft), so a typed
// letter re-runs that row's props and never rebuilds the list (an input that
// is rebuilt loses its caret).
export function gettingStartedTeamRows(root) {
  const r = _gstRootOf(root, this)
  return ((r && r.gettingStartedPeople) || []).map((p) => ({
    personId: p.personId,
    personName: p.personName,
    personRole: p.personRole,
    personSource: p.personSource,
    invitedAt: p.invitedAt || null
  }))
}

// One person's draft: { email, on, status, error } — never undefined fields.
export function gettingStartedDraft(root, personId) {
  return _gstDraftOf(_gstRootOf(root, this), personId)
}

export function gettingStartedEmailOk(value) {
  return _gstEmailOk(value)
}

// The "company" tick: the typed address is on the researched domain (or one
// of its subdomains).
export function gettingStartedOnDomain(root, value) {
  const r = _gstRootOf(root, this)
  const domain = String((r && r.gettingStartedDomain) || '')
    .toLowerCase()
    .replace(/^www\./, '')
  const email = String(value || '')
    .trim()
    .toLowerCase()
  if (!domain || !_gstEmailOk(email)) return false
  const host = email.split('@').pop()
  return host === domain || host.endsWith(`.${domain}`)
}

// Typing writes the draft; it never turns a row ON, and an address made
// invalid turns its row OFF. The raw text is kept (trimming it back into the
// field would fight the caret); the send trims it.
export function setGettingStartedEmail(root, personId, value) {
  const r = _gstRootOf(root, this)
  if (!r || !personId) return
  const cur = _gstDraftOf(r, personId)
  const email = String(value == null ? '' : value)
  r.update({
    gettingStartedInvites: {
      ...(r.gettingStartedInvites || {}),
      [personId]: { email, on: cur.on && _gstEmailOk(email), status: '', error: '' }
    }
  })
}

// The row's switch: ON only beside a valid typed address, never for a person
// already invited.
export function toggleGettingStartedInvite(root, personId) {
  const r = _gstRootOf(root, this)
  if (!r || !personId) return
  const person = ((r && r.gettingStartedPeople) || []).find((p) => p.personId === personId)
  if (!person || person.invitedAt) return
  const cur = _gstDraftOf(r, personId)
  r.update({
    gettingStartedInvites: {
      ...(r.gettingStartedInvites || {}),
      [personId]: { email: cur.email, on: !cur.on && _gstEmailOk(cur.email), status: '', error: '' }
    }
  })
}

export function setGettingStartedOtherEmail(root, value) {
  const r = _gstRootOf(root, this)
  if (!r) return
  r.update({
    gettingStartedOther: { email: String(value == null ? '' : value), status: '', error: '' }
  })
}

// How many invites "Send invites (N)" sends now.
export function gettingStartedInviteCount(root) {
  const r = _gstRootOf(root, this)
  if (!r) return 0
  const rows = ((r && r.gettingStartedPeople) || []).filter((p) => {
    const d = _gstDraftOf(r, p.personId)
    return !p.invitedAt && d.on && _gstEmailOk(d.email)
  })
  const other = (r.gettingStartedOther && r.gettingStartedOther.email) || ''
  return rows.length + (_gstEmailOk(other) ? 1 : 0)
}

// "Send invites (N)": one plain invite per ON row, from the owner, through
// the onboarding method (the server marks the row used); someone else
// through the workspace invite. Resolves { sent, failed }, or null when
// nothing was ON.
export async function sendGettingStartedInvites(root, via) {
  const r = _gstRootOf(root, this)
  if (!r || r.gettingStartedSending) return null
  const seam = via && typeof via === 'object' ? via : {}
  const wsId = _gstWsId(r)
  if (!wsId) return null
  const sdk = _gstSdk(seam, this)
  const write = typeof seam.write === 'function' ? seam.write : (patch) => r.update(patch)
  const picked = ((r && r.gettingStartedPeople) || []).filter((p) => {
    const d = _gstDraftOf(r, p.personId)
    return !p.invitedAt && d.on && _gstEmailOk(d.email)
  })
  const otherEmail = String((r.gettingStartedOther && r.gettingStartedOther.email) || '').trim()
  const sendOther = _gstEmailOk(otherEmail)
  if (!picked.length && !sendOther) return null

  const sending = { ...(r.gettingStartedInvites || {}) }
  picked.forEach((p) => {
    sending[p.personId] = { ..._gstDraftOf(r, p.personId), status: 'sending', error: '' }
  })
  write({
    gettingStartedSending: true,
    gettingStartedSendResult: null,
    gettingStartedInvites: sending
  })

  const results = await Promise.all(
    picked.map(async (p) => {
      const d = _gstDraftOf(r, p.personId)
      if (!_gstHas(sdk, 'inviteOnboardingPerson')) return { p, d, ok: false, code: 'unavailable' }
      try {
        const res = await sdk.inviteOnboardingPerson(wsId, p.personId, {
          email: d.email.trim(),
          recipientName: p.personName
        })
        const person = (res && (res.person || (res.data && res.data.person))) || {}
        return { p, d, ok: true, invitedAt: person.invitedAt || new Date().toISOString() }
      } catch (err) {
        console.error('[getting-started/team] invite', err)
        return { p, d, ok: false, code: _gstCode(err) || 'failed' }
      }
    })
  )
  let otherResult = null
  if (sendOther) {
    try {
      await sdk.createWorkspaceInvitation(wsId, { email: otherEmail, role: 'editor' })
      otherResult = { ok: true }
    } catch (err) {
      console.error('[getting-started/team] invite (someone else)', err)
      otherResult = { ok: false, code: _gstCode(err) || 'failed' }
    }
  }
  // A switch landed mid-send: the new workspace's slice is not ours to write.
  if (_gstWsId(r) !== wsId) return null

  const drafts = { ...(r.gettingStartedInvites || {}) }
  const invitedAt = {}
  results.forEach((x) => {
    if (x.ok) {
      invitedAt[x.p.personId] = x.invitedAt
      drafts[x.p.personId] = { email: '', on: false, status: 'sent', error: '' }
    } else {
      drafts[x.p.personId] = { email: x.d.email, on: true, status: 'failed', error: x.code }
    }
  })
  const sent = results.filter((x) => x.ok).length + (otherResult && otherResult.ok ? 1 : 0)
  const failed =
    results.length - results.filter((x) => x.ok).length + (otherResult && !otherResult.ok ? 1 : 0)
  write({
    gettingStartedSending: false,
    gettingStartedPeople: ((r && r.gettingStartedPeople) || []).map((p) =>
      invitedAt[p.personId] ? { ...p, invitedAt: invitedAt[p.personId] } : p
    ),
    gettingStartedInvites: drafts,
    ...(otherResult
      ? {
          gettingStartedOther: otherResult.ok
            ? { email: '', status: 'sent', error: '' }
            : { email: otherEmail, status: 'failed', error: otherResult.code }
        }
      : {}),
    gettingStartedSendResult: { sent, failed }
  })
  return { sent, failed }
}

// "Remove": the server deletes the researched row (it was never the owner's
// data to keep), then the row leaves the list. Resolves true when it did.
export async function removeGettingStartedPerson(root, personId, via) {
  const r = _gstRootOf(root, this)
  if (!r || !personId) return false
  const seam = via && typeof via === 'object' ? via : {}
  const wsId = _gstWsId(r)
  const sdk = _gstSdk(seam, this)
  if (!wsId || !_gstHas(sdk, 'removeOnboardingPerson')) return false
  const write = typeof seam.write === 'function' ? seam.write : (patch) => r.update(patch)
  const cur = _gstDraftOf(r, personId)
  write({
    gettingStartedInvites: {
      ...(r.gettingStartedInvites || {}),
      [personId]: { ...cur, status: 'removing', error: '' }
    }
  })
  try {
    await sdk.removeOnboardingPerson(wsId, personId)
  } catch (err) {
    console.error('[getting-started/team] remove', err)
    if (_gstWsId(r) === wsId) {
      write({
        gettingStartedInvites: {
          ...(r.gettingStartedInvites || {}),
          [personId]: { ...cur, status: 'failed', error: 'removeFailed' }
        }
      })
    }
    return false
  }
  if (_gstWsId(r) !== wsId) return false
  write({
    gettingStartedPeople: ((r && r.gettingStartedPeople) || []).filter(
      (p) => p.personId !== personId
    ),
    gettingStartedInvites: {
      ...(r.gettingStartedInvites || {}),
      [personId]: { email: '', on: false, status: '', error: '' }
    }
  })
  return true
}

// "Anyone with an @<domain> e-mail can join (they ask, you approve)" — only
// for an eligible owner; the server answers 409 for anyone else.
export async function setGettingStartedDomainJoin(root, enabled, via) {
  const r = _gstRootOf(root, this)
  const cur = r && r.gettingStartedDomainJoin
  if (!r || !cur || !cur.eligible) return
  const seam = via && typeof via === 'object' ? via : {}
  const wsId = _gstWsId(r)
  const sdk = _gstSdk(seam, this)
  if (!wsId || !_gstHas(sdk, 'setDomainJoin')) return
  const write = typeof seam.write === 'function' ? seam.write : (patch) => r.update(patch)
  write({ gettingStartedDomainJoinStatus: 'saving' })
  try {
    const res = (await sdk.setDomainJoin(wsId, !!enabled)) || {}
    if (_gstWsId(r) !== wsId) return
    write({
      gettingStartedDomainJoin: {
        domain: String(res.domain || cur.domain || ''),
        enabled: !!res.enabled,
        eligible: res.eligible !== false
      },
      gettingStartedDomainJoinStatus: ''
    })
  } catch (err) {
    console.error('[getting-started/team] domain join', err)
    if (_gstWsId(r) !== wsId) return
    const refused = err && (err.status === 409 || _gstCode(err) === 'domain_not_eligible')
    write({ gettingStartedDomainJoinStatus: refused ? 'notEligible' : 'failed' })
  }
}

export function toggleGettingStartedWhy(root) {
  const r = _gstRootOf(root, this)
  if (!r) return
  r.update({ _gettingStartedWhyOpen: !r._gettingStartedWhyOpen })
}

// The page the names came from — "People on bessi.ge/about ↗".
export function gettingStartedTeamSource(root) {
  const r = _gstRootOf(root, this)
  const first = ((r && r.gettingStartedPeople) || []).find((p) => p.personSource)
  const url = (first && first.personSource) || ''
  if (!url) return { url: '', label: '' }
  try {
    const u = new URL(url)
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '')
    return { url, label: `${u.host.replace(/^www\./, '')}${path}` }
  } catch (e) {
    return { url, label: url }
  }
}

// Which Getting-started surface an href names: 'team' | 'imports' | null.
// The switch tail asks it of the URL path and of each pane's href (a pane
// href may carry a query).
export function gettingStartedSurfaceFor(href) {
  const path = String(href || '')
    .split('?')[0]
    .split('#')[0]
  if (path === '/getting-started/team') return 'team'
  if (path === '/getting-started/imports') return 'imports'
  return null
}
